/**
 * KorgMS20 - Emulation du synthétiseur Korg MS-20
 *
 * Caractéristiques distinctives :
 * - 2 VCOs avec ring modulation
 * - Double filtre en série : HPF → LPF (son agressif unique)
 * - Filtre Korg 35 avec résonance qui "crie"
 * - 2 enveloppes (EG1 pour filtre, EG2 pour VCA)
 */

import * as Tone from 'tone';

export class KorgMS20 {
    constructor(audioEngine, options = {}) {
        this.audioEngine = audioEngine;
        this.performance = options.performance || { lowPower: false };
        this.currentNote = null;

        // État power (pour économiser les ressources)
        this.isPowered = true;
        this.mixerChannel = null;

        // Paramètres par défaut
        this.params = {
            // VCO1
            vco1Waveform: 'sawtooth',
            vco1Octave: 0,
            vco1Level: 0.8,
            // VCO2
            vco2Waveform: 'sawtooth',
            vco2Octave: 0,
            vco2Detune: 0,
            vco2Level: 0.6,
            // Ring Mod
            ringModLevel: 0,
            // Noise
            noiseLevel: 0,
            // High Pass Filter
            hpfCutoff: 20,
            hpfResonance: 0,
            // Low Pass Filter (le filtre qui crie!)
            lpfCutoff: 5000,
            lpfResonance: 1,
            lpfEnvAmount: 0.5,
            lpfKeyTrack: 0.5,
            // EG1 (Filter Envelope)
            eg1Attack: 0.01,
            eg1Decay: 0.3,
            eg1Sustain: 0.4,
            eg1Release: 0.3,
            // EG2 (Amp Envelope)
            eg2Attack: 0.01,
            eg2Decay: 0.2,
            eg2Sustain: 0.7,
            eg2Release: 0.3,
            // Modulation Generator (LFO)
            mgRate: 5,
            mgPitchAmount: 0,
            mgFilterAmount: 0,
            // Master
            portamento: 0,
            volume: 0.7
        };

        this._createSynth();
    }

    _createSynth() {
        // === VCO1 ===
        this.vco1 = new Tone.Oscillator({
            type: this.params.vco1Waveform,
            frequency: 440
        });
        this.vco1Gain = new Tone.Gain(this.params.vco1Level);

        // === VCO2 ===
        this.vco2 = new Tone.Oscillator({
            type: this.params.vco2Waveform,
            frequency: 440
        });
        this.vco2Gain = new Tone.Gain(this.params.vco2Level);

        // === Ring Modulator ===
        this.ringMod = new Tone.Multiply();
        this.ringModGain = new Tone.Gain(this.params.ringModLevel);

        // === Noise ===
        this.noise = new Tone.Noise('white');
        this.noiseGain = new Tone.Gain(this.params.noiseLevel);

        // === Mixer ===
        this.mixer = new Tone.Gain(1);

        // Connexions VCO → Mixer
        this.vco1.connect(this.vco1Gain);
        this.vco2.connect(this.vco2Gain);
        this.noise.connect(this.noiseGain);

        this.vco1Gain.connect(this.mixer);
        this.vco2Gain.connect(this.mixer);
        this.noiseGain.connect(this.mixer);

        // Ring Mod : VCO1 * VCO2
        this.vco1.connect(this.ringMod);
        this.vco2.connect(this.ringMod.factor);
        this.ringMod.connect(this.ringModGain);
        this.ringModGain.connect(this.mixer);

        // === HPF (High Pass Filter) - Premier filtre ===
        this.hpf = new Tone.Filter({
            type: 'highpass',
            frequency: this.params.hpfCutoff,
            rolloff: -12,
            Q: this.params.hpfResonance
        });

        // === LPF (Low Pass Filter) - Deuxième filtre (le son MS-20!) ===
        // Utilise rolloff -24 pour simuler le Korg 35
        this.lpf = new Tone.Filter({
            type: 'lowpass',
            frequency: this.params.lpfCutoff,
            rolloff: -24,
            Q: this.params.lpfResonance
        });

        // === Saturation pour le caractère agressif du filtre ===
        this.filterDrive = new Tone.Distortion({
            distortion: 0.1,
            wet: 0.3
        });

        // === Amp Envelope (EG2) ===
        this.ampEnv = new Tone.AmplitudeEnvelope({
            attack: this.params.eg2Attack,
            decay: this.params.eg2Decay,
            sustain: this.params.eg2Sustain,
            release: this.params.eg2Release
        });

        // === LFO (MG - Modulation Generator) ===
        this.mg = new Tone.LFO({
            frequency: this.params.mgRate,
            min: -1,
            max: 1
        });
        this.mgPitchGain = new Tone.Gain(this.params.mgPitchAmount * 50); // En cents
        this.mgFilterGain = new Tone.Gain(this.params.mgFilterAmount * 2000); // En Hz

        // === Master Volume ===
        this.masterGain = new Tone.Gain(this.params.volume);

        // === Chaîne audio : Mixer → HPF → LPF → Drive → AmpEnv → Master ===
        this.mixer.connect(this.hpf);
        this.hpf.connect(this.lpf);
        this.lpf.connect(this.filterDrive);
        this.filterDrive.connect(this.ampEnv);
        this.ampEnv.connect(this.masterGain);

        // === LFO connections ===
        this.mg.connect(this.mgPitchGain);
        this.mg.connect(this.mgFilterGain);

        // Connecter LFO au filtre LPF (modulation additive sur la fréquence)
        this.mgFilterGain.connect(this.lpf.frequency);

        // Connecter LFO au pitch des oscillateurs
        this.mgPitchGain.connect(this.vco1.frequency);
        this.mgPitchGain.connect(this.vco2.frequency);

        // === Connecter au mixer global ===
        this.mixerChannel = this.audioEngine.getMixerChannelInput('ms20');
        if (this.mixerChannel) {
            this.masterGain.connect(this.mixerChannel);
        } else {
            this.masterGain.connect(this.audioEngine.getMasterVolume());
        }

        // === Démarrer les oscillateurs et LFO ===
        this._startOscillators();
    }

    /**
     * Démarre les oscillateurs et LFO
     */
    _startOscillators() {
        if (this.vco1?.state !== 'started') this.vco1.start();
        if (this.vco2?.state !== 'started') this.vco2.start();
        if (this.noise?.state !== 'started') this.noise.start();
        if (this.mg?.state !== 'started') this.mg.start();
    }

    /**
     * Arrête les oscillateurs et LFO
     */
    _stopOscillators() {
        if (this.vco1?.state === 'started') this.vco1.stop();
        if (this.vco2?.state === 'started') this.vco2.stop();
        if (this.noise?.state === 'started') this.noise.stop();
        if (this.mg?.state === 'started') this.mg.stop();
    }

    /**
     * Calcule la fréquence avec octave
     */
    _calcFrequency(baseFreq, octave) {
        return baseFreq * Math.pow(2, octave);
    }

    /**
     * Joue une note
     */
    triggerAttack(note, time, velocity = 1) {
        const freq = Tone.Frequency(note).toFrequency();
        const now = time || Tone.now();

        // Portamento
        const glideTime = this.params.portamento;

        // VCO1
        this.vco1.frequency.rampTo(
            this._calcFrequency(freq, this.params.vco1Octave),
            glideTime,
            now
        );

        // VCO2 avec detune
        const vco2Freq = this._calcFrequency(freq, this.params.vco2Octave);
        const detuneRatio = Math.pow(2, this.params.vco2Detune / 1200);
        this.vco2.frequency.rampTo(vco2Freq * detuneRatio, glideTime, now);

        // Keyboard tracking pour LPF
        const keyTrackAmount = this.params.lpfKeyTrack;
        const baseFreqRatio = freq / 261.63; // Ratio par rapport à C4
        const keyTrackOffset = (baseFreqRatio - 1) * keyTrackAmount * 3000;

        // EG1 → LPF (envelope manuelle)
        const lpfBase = this.params.lpfCutoff + keyTrackOffset;
        const lpfEnvAmount = this.params.lpfEnvAmount * 8000;
        const lpfPeak = Math.min(lpfBase + lpfEnvAmount, 18000);
        const lpfSustainFreq = lpfBase + (lpfEnvAmount * this.params.eg1Sustain);

        this.lpf.frequency.cancelScheduledValues(now);
        this.lpf.frequency.setValueAtTime(lpfBase, now);
        this.lpf.frequency.linearRampToValueAtTime(lpfPeak, now + this.params.eg1Attack);
        this.lpf.frequency.linearRampToValueAtTime(
            lpfSustainFreq,
            now + this.params.eg1Attack + this.params.eg1Decay
        );

        // EG2 → VCA
        this.ampEnv.triggerAttack(now, velocity);

        this.currentNote = note;
    }

    /**
     * Relâche la note
     */
    triggerRelease(time) {
        const now = time || Tone.now();
        this.ampEnv.triggerRelease(now);

        // Release du filtre
        this.lpf.frequency.linearRampToValueAtTime(
            this.params.lpfCutoff,
            now + this.params.eg1Release
        );
    }

    /**
     * Joue une note avec durée
     */
    triggerAttackRelease(note, duration, time, velocity = 1) {
        const now = time || Tone.now();
        this.triggerAttack(note, now, velocity);
        const releaseTime = now + Tone.Time(duration).toSeconds();
        this.triggerRelease(releaseTime);
    }

    // ========== SETTERS ==========

    // VCO1
    setVco1Waveform(waveform) {
        this.params.vco1Waveform = waveform;
        this.vco1.type = waveform;
    }

    setVco1Octave(octave) {
        this.params.vco1Octave = octave;
    }

    setVco1Level(level) {
        this.params.vco1Level = level;
        this.vco1Gain.gain.value = level;
    }

    // VCO2
    setVco2Waveform(waveform) {
        this.params.vco2Waveform = waveform;
        this.vco2.type = waveform;
    }

    setVco2Octave(octave) {
        this.params.vco2Octave = octave;
    }

    setVco2Detune(cents) {
        this.params.vco2Detune = cents;
    }

    setVco2Level(level) {
        this.params.vco2Level = level;
        this.vco2Gain.gain.value = level;
    }

    // Ring Mod
    setRingModLevel(level) {
        this.params.ringModLevel = level;
        this.ringModGain.gain.value = level;
    }

    // Noise
    setNoiseLevel(level) {
        this.params.noiseLevel = level;
        this.noiseGain.gain.value = level;
    }

    // HPF
    setHpfCutoff(freq) {
        this.params.hpfCutoff = freq;
        this.hpf.frequency.value = freq;
    }

    setHpfResonance(q) {
        this.params.hpfResonance = q;
        this.hpf.Q.value = q;
    }

    // LPF
    setLpfCutoff(freq) {
        this.params.lpfCutoff = freq;
        this.lpf.frequency.value = freq;
    }

    setLpfResonance(q) {
        this.params.lpfResonance = q;
        this.lpf.Q.value = q;
        // Ajuster la saturation selon la résonance pour le caractère MS-20
        this.filterDrive.distortion = 0.1 + (q / 30) * 0.4;
        this.filterDrive.wet.value = 0.3 + (q / 30) * 0.4;
    }

    setLpfEnvAmount(amount) {
        this.params.lpfEnvAmount = amount;
    }

    setLpfKeyTrack(amount) {
        this.params.lpfKeyTrack = amount;
    }

    // EG1 (Filter)
    setEg1Attack(time) {
        this.params.eg1Attack = time;
    }

    setEg1Decay(time) {
        this.params.eg1Decay = time;
    }

    setEg1Sustain(level) {
        this.params.eg1Sustain = level;
    }

    setEg1Release(time) {
        this.params.eg1Release = time;
    }

    // EG2 (Amp)
    setEg2Attack(time) {
        this.params.eg2Attack = time;
        this.ampEnv.attack = time;
    }

    setEg2Decay(time) {
        this.params.eg2Decay = time;
        this.ampEnv.decay = time;
    }

    setEg2Sustain(level) {
        this.params.eg2Sustain = level;
        this.ampEnv.sustain = level;
    }

    setEg2Release(time) {
        this.params.eg2Release = time;
        this.ampEnv.release = time;
    }

    // MG (LFO)
    setMgRate(rate) {
        this.params.mgRate = rate;
        this.mg.frequency.value = rate;
    }

    setMgPitchAmount(amount) {
        this.params.mgPitchAmount = amount;
        this.mgPitchGain.gain.value = amount * 50;
    }

    setMgFilterAmount(amount) {
        this.params.mgFilterAmount = amount;
        this.mgFilterGain.gain.value = amount * 2000;
    }

    // Master
    setPortamento(time) {
        this.params.portamento = time;
    }

    setVolume(level) {
        this.params.volume = level;
        this.masterGain.gain.value = level;
    }

    // ========== GETTERS ==========
    getParams() {
        return { ...this.params };
    }

    setParams(params) {
        Object.entries(params).forEach(([key, value]) => {
            // Convert param name to setter name (e.g., 'lpfCutoff' -> 'setLpfCutoff')
            const setter = `set${key.charAt(0).toUpperCase() + key.slice(1)}`;
            if (typeof this[setter] === 'function') {
                this[setter](value);
            }
        });
    }

    // --- Power ON/OFF ---

    setPower(on) {
        if (on === this.isPowered) return;

        this.isPowered = on;

        if (on) {
            if (this.mixerChannel) {
                this.masterGain.connect(this.mixerChannel);
            }
            this._startOscillators();
        } else {
            this.masterGain.disconnect();
            this._stopOscillators();
        }
    }

    getPower() {
        return this.isPowered;
    }
}
